import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlusCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { useAntMessage } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { Button } from '@/shared/ui/button';

import { useCreatePatternWatchlistMutation } from '../../api/pattern-watchlist.api';
import type { FormValues } from '../pattern-watchlist-form/pattern-watchlist-form';
import { PatternWatchlistModal } from '../pattern-watchlist-modal/pattern-watchlist-modal';

import cls from './empty-pattern-watchlist.module.css';

export const EmptyPatternWatchlist = memo(() => {
    const { t } = useTranslation('pattern-watchlist');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [createWatchlist, { isLoading, isSuccess }] =
        useCreatePatternWatchlistMutation();
    const { success } = useAntMessage();

    const onOpenModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const onCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const onSubmit = useCallback(
        async (values: FormValues) => {
            try {
                await createWatchlist({ name: values.name });

                success?.({
                    content: t('createSuccess'),
                });

                onCloseModal();
            } catch (error) {
                logError('Failed to create watchlist:', error);
            }
        },
        [createWatchlist, onCloseModal, success, t],
    );

    return (
        <div className={classNames(cls.root, 'algotech-watchlist-empty')}>
            <div className={cls.content}>
                <h3 className={cls.title}>
                    {t('pattern-studio:emptyState.watchlist.title')}
                </h3>

                <p>{t('pattern-studio:emptyState.watchlist.description')}</p>

                <Button
                    icon={<PlusCircleOutlined />}
                    onClick={onOpenModal}
                    title={t(
                        'pattern-studio:emptyState.watchlist.createButton',
                    )}
                />
            </div>

            <PatternWatchlistModal
                isLoading={isLoading}
                isOpen={isModalOpen}
                isSuccess={isSuccess}
                onClose={onCloseModal}
                onSubmit={onSubmit}
            />
        </div>
    );
});

EmptyPatternWatchlist.displayName = 'CreatePatternWatchlist';
